import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Users, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PartitarioPage() {
  const [kind, setKind] = useState('clienti');
  const [data, setData] = useState({ items: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async (k) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/fatturazione/partitario?kind=${k}`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error('Errore caricamento partitario:', err);
      toast.error('Errore caricamento partitario');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(kind); }, [fetchData, kind]);

  const fmt = (n) => (n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 });

  // Helpers to avoid nested ternaries in JSX
  const getSaldoClass = (saldo) => {
    if (saldo > 0) return 'text-blue-700';
    if (saldo < 0) return 'text-red-700';
    return 'text-slate-600';
  };
  const kindLabel = kind === 'clienti' ? 'cliente' : 'fornitore';
  const kindHeader = kind === 'clienti' ? 'Cliente' : 'Fornitore';

  const renderTableBody = () => {
    if (loading) {
      return <TableRow><TableCell colSpan={8} className="text-center py-10">Caricamento...</TableCell></TableRow>;
    }
    if (data.items.length === 0) {
      return <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-500">Nessun {kindLabel} con movimenti</TableCell></TableRow>;
    }
    return data.items.map((r) => (
      <TableRow key={r.controparte} data-testid={`partitario-row-${r.controparte}`}>
        <TableCell className="font-medium">{r.controparte}</TableCell>
        <TableCell className="text-right text-sm">{r.n_fatture}</TableCell>
        <TableCell className="text-right text-sm text-amber-700">{r.n_note_credito}</TableCell>
        <TableCell className="text-right">€ {fmt(r.totale_fatturato)}</TableCell>
        <TableCell className="text-right text-amber-700">€ {fmt(r.totale_note_credito)}</TableCell>
        <TableCell className="text-right text-emerald-700">€ {fmt(r.totale_pagato)}</TableCell>
        <TableCell className="text-right text-red-700">€ {fmt(r.totale_da_pagare)}</TableCell>
        <TableCell className={`text-right font-bold ${getSaldoClass(r.saldo)}`}>€ {fmt(r.saldo)}</TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="space-y-6" data-testid="partitario-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
          <Users className="h-6 w-6" />Partitario
        </h1>
        <p className="text-sm text-slate-500 mt-1">Saldi e movimenti per cliente / fornitore</p>
      </div>

      <Tabs value={kind} onValueChange={setKind}>
        <TabsList>
          <TabsTrigger value="clienti" data-testid="tab-clienti"><Users className="h-4 w-4 mr-1.5" />Clienti</TabsTrigger>
          <TabsTrigger value="fornitori" data-testid="tab-fornitori"><Building2 className="h-4 w-4 mr-1.5" />Fornitori</TabsTrigger>
        </TabsList>
        <TabsContent value={kind} className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs uppercase">{kindHeader}</TableHead>
                    <TableHead className="text-xs uppercase text-right">N. Fatture</TableHead>
                    <TableHead className="text-xs uppercase text-right">N. Note Credito</TableHead>
                    <TableHead className="text-xs uppercase text-right">Tot. Fatturato</TableHead>
                    <TableHead className="text-xs uppercase text-right">Tot. NC</TableHead>
                    <TableHead className="text-xs uppercase text-right">Pagato</TableHead>
                    <TableHead className="text-xs uppercase text-right">Da Pagare</TableHead>
                    <TableHead className="text-xs uppercase text-right">Saldo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {renderTableBody()}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
